/**
 * PortalShell — new portal-native tab container (Phase 3.1).
 * Tabs: Run (default) | Assets | People
 * Alongside existing PlanTabContainer (overview/tasks/when/outputs) for backward compat.
 */

import React, { useState, useCallback } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { Plan } from '../types/generated/graphql';
import { RunTab } from './portal-tabs/RunTab';
import { AssetsTab } from './portal-tabs/AssetsTab';
import { PeopleTab } from './portal-tabs/PeopleTab';
import StepDetailDialog from './StepDetailDialog';
import DelegationPanel from './DelegationPanel';
import ClonePortalModal from './ClonePortalModal';
import { trackPortalEvent } from '../utils/portalAnalytics';

export type PortalTab = 'run' | 'assets' | 'people';

interface PortalShellProps {
  plan: Plan;
  currentTab: PortalTab;
  onTabChange: (tab: PortalTab) => void;
  onToggleStepCompletion: (stepId: string, completed: boolean) => void;
}

const TAB_INDEX: Record<PortalTab, number> = { run: 0, assets: 1, people: 2 };
const INDEX_TAB: PortalTab[] = ['run', 'assets', 'people'];

export const PortalShell: React.FC<PortalShellProps> = ({
  plan,
  currentTab,
  onTabChange,
  onToggleStepCompletion,
}) => {
  const [stepDialogOpen, setStepDialogOpen] = useState(false);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [delegationOpen, setDelegationOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [delegationStepTitle, setDelegationStepTitle] = useState('');
  const [cloneOpen, setCloneOpen] = useState(false);

  const selectedStep = selectedStepId
    ? plan.steps.find((s) => s.id === selectedStepId) ?? null
    : null;

  const handleStepClick = useCallback((stepId: string) => {
    setSelectedStepId(stepId);
    setStepDialogOpen(true);
    trackPortalEvent('step_view', { portalId: plan.id, stepId });
  }, [plan.id]);

  const handleCloseStep = useCallback(() => {
    setStepDialogOpen(false);
    setSelectedStepId(null);
  }, []);

  const handleTabSwitch = (_: React.SyntheticEvent, idx: number) => {
    const tab = INDEX_TAB[idx];
    onTabChange(tab);
    trackPortalEvent('portal_tab_switch', { portalId: plan.id, metadata: { tab } });
  };

  return (
    <Box>
      {/* Tab bar */}
      <Tabs
        value={TAB_INDEX[currentTab]}
        onChange={handleTabSwitch}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        <Tab label="Run" sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab label="Assets" sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab label="People" sx={{ textTransform: 'none', fontWeight: 600 }} />
      </Tabs>

      {/* Tab panels */}
      {currentTab === 'run' && (
        <RunTab plan={plan} onStepClick={handleStepClick} onToggleCompletion={onToggleStepCompletion} />
      )}
      {currentTab === 'assets' && <AssetsTab plan={plan} />}
      {currentTab === 'people' && <PeopleTab plan={plan} />}

      {/* Step Detail Dialog */}
      <StepDetailDialog
        open={stepDialogOpen}
        step={selectedStep}
        planId={plan.id}
        onClose={handleCloseStep}
        onToggleCompletion={onToggleStepCompletion}
      />

      {/* Delegation Panel */}
      <DelegationPanel
        open={delegationOpen}
        stepTitle={delegationStepTitle}
        onClose={() => setDelegationOpen(false)}
        onAssign={(data) => {
          trackPortalEvent('delegation_send', { portalId: plan.id, metadata: data });
          setDelegationOpen(false);
        }}
      />

      {/* Clone Modal */}
      <ClonePortalModal
        open={cloneOpen}
        portalName={plan.goal}
        onClose={() => setCloneOpen(false)}
        onClone={(data) => {
          // TODO: wire to backend duplicate mutation
          console.log('Clone portal:', data);
          setCloneOpen(false);
        }}
      />
    </Box>
  );
};

export default PortalShell;
